import * as React from 'react';
import './contextualizeModal.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { useActions } from './contextualizeModalActions';
import { useActions as useMetadataActions } from '../metadataFormActions';
import { TriggerLocation } from '../../../enums/TriggerLocation';
import { Metadata, Schema } from '../../../types';
import { SubType } from '../../../api/getResearchModels';
import URLManager from '../../../utils/urlManager';
import { Typeahead } from '../../Typeahead/typeahead';
import { SROpts } from '../../../types/SROpts';
import {
  getFilteredDataSourcesAndProjects,
  getItemContentTypeId,
  getSchema,
  useAorAn
} from '../../../utils';
import { Error } from 'rcd-cpp-ui/ui/alert';
import { ItemLink } from '../../ItemLink/itemLink';
import { RelationshipTypeId } from '../../../enums/RelationshipTypeId';
import { useLoader } from '../../Loader/loader';
import { ItemType } from '../../../enums/ItemType';
import { getAndFlattenCustomFieldData } from '../../../utils';
import { ContextualizationConfig } from '../../../types/ContentTypeConfiguration';
import { ContentType as ItemContentType } from '../../../enums/ContentType';
import { ContentSubType } from '../../../enums/ContentSubType';


export enum ContextualizeModalType {
  SelectLinkType = 'SELECT_LINK_TYPE',
  None = 'NONE'
}

export enum ContextualizeType {
  Perspective = 'Perspective',
  Variant = 'Variant',
  Branch = 'Branch'
}

const contextualizeOptions = [
  {
    type: ContextualizeType.Branch,
    configKey: 'branch'
  },
  {
    type: ContextualizeType.Perspective,
    configKey: 'perspective'
  },
  {
    type: ContextualizeType.Variant,
    configKey: 'variant'
  }
];

const MQCCUpdateSubTypes = [ContentSubType.MQUpdate, ContentSubType.CCUpdate];

type Props = {
  onCancel: () => any;
  data: Partial<Metadata>;
  schemaForm: Schema;
  schema: Schema;
};

export const ContextualizeModal = ({
  data,
  schemaForm,
  schema,
  onCancel
}: Props) => {
  const {
    isLoading,
    Loader,
    getContextualizationChildren,
    loadContextualizationContentSubType,
    loadSupportedTypes,
    loadSupportedBranchTypes,
    getSubtypeContextList
  } = useLoader(useActions());
  const { showForm } = useMetadataActions();
  const { updateUrl } = URLManager.useRouter();
  const [selectedType, setSelectedType] = React.useState(null);
  const [supportedContextualizationTypes, setSupportedContextualizationTypes] =
    React.useState<ContextualizationConfig>(null);
  const [supportedSubtypes, setSupportedSubtypes] =
    React.useState<SubType[]>(null);
  const [selectedSubType, setSelectedSubType] = React.useState<SubType>(null);
  const [supportedSubtypeContexts, setSupportedSubtypeContexts] =
    React.useState<SubType[]>(null);
  const [selectedSubTypeContext, setSelectedSubTypeContext] =
    React.useState<SubType>(null);
  const [contextChildren, setContextChildren] =
    React.useState<Metadata[]>(null);
  const [contentSubTypeList, setContentSubTypeList] =
    React.useState<Metadata[]>(null);

  const parentItemId = data?.props?.itemId;
  const parentRefId = data?.props?.refId;
  const itemContentTypeId = getItemContentTypeId(data);

  const disabledContentType =
    itemContentTypeId == ItemContentType.Radar_Profile ||
    itemContentTypeId == ItemContentType.INNOVATION_PROFILE;

  React.useEffect(() => {
    if (data?.props?.contentType) {
      loadContextualizationContentSubType().then(result => {
        setContentSubTypeList(result);
      });
    }
  }, [data]);

  const getContentTypeOrSubType = React.useCallback(
    (refId: string): Metadata => {
      return contentSubTypeList?.find(n => n.props.refId === refId);
    },
    [contentSubTypeList]
  );

  React.useEffect(() => {
    if (parentItemId) {
      loadSupportedTypes(parentItemId).then(result => {
        if (result) setSupportedContextualizationTypes(result);
      });
    }
  }, [parentItemId]);

  React.useEffect(() => {
    if (
      selectedType &&
      selectedType == ContextualizeType.Branch &&
      (!supportedSubtypes || !supportedSubtypeContexts)
    ) {
      getContextualizationChildren(parentRefId).then(children => {
        setContextChildren(children);
      });
      loadSupportedBranchTypes(
        data,
        supportedContextualizationTypes?.branch
      ).then(supportedSubTypes => {
        if (supportedSubTypes) setSupportedSubtypes(supportedSubTypes);
      });
    }
  }, [selectedType]);
  React.useEffect(() => {
    if (selectedSubType) {
      getSubtypeContextList(selectedSubType).then(results => {
        if (results) {
          setSupportedSubtypeContexts(results);
        }
      });
    }
  }, [selectedSubType]);

  const getCreateItemData = (): Partial<Metadata> => {
    const customFieldData = getAndFlattenCustomFieldData(data, schemaForm);

    const contentType =
      selectedType == ContextualizeType.Perspective
        ? [getContentTypeOrSubType('dtyp:281')]
        : selectedType == ContextualizeType.Branch &&
            !data?.props?.contentType?.some(
              ct => ct?.props?.id == ItemContentType.INNOVATION_PROFILE
            )
          ? [getContentTypeOrSubType('dtyp:184')]
          : data?.props?.contentType;
    const contentSubtype =
      selectedType == ContextualizeType.Perspective
        ? undefined
        : selectedType == ContextualizeType.Branch &&
            !data?.props?.contentType?.some(
              ct => ct?.props?.id == ItemContentType.INNOVATION_PROFILE
            )
          ? [getContentTypeOrSubType(`dstp:184_${selectedSubType.id}`)]
          : data?.props?.contentSubtype;
    const contextType =
      selectedType == ContextualizeType.Branch && selectedSubTypeContext?.id
        ? [
            {
              props: {
                refId: `dctxt:${selectedSubType.id}_${selectedSubTypeContext.id}`,
                id: selectedSubTypeContext.id,
                description: selectedSubTypeContext.description
              }
            }
          ]
        : undefined;

    const { parent, parentField, titlePreceedingText } =
      createAsContextualizationParent(
        data,
        selectedType,
        selectedSubType,
        selectedSubTypeContext
      );
    const { filteredDataSources, filteredProjects, filteredDataSourceItems } =
      getFilteredDataSourcesAndProjects(data);
    return {
      props: {
        ...customFieldData,
        itemType: ItemType.content,
        title: `${titlePreceedingText}${data?.props?.title}`,
        summary: data?.props?.summary,
        function: data?.props?.function,
        role: data?.props?.role,
        primaryQuestion: data?.props?.primaryQuestion,
        additionalQuestions: data?.props?.additionalQuestions,
        additionalFunctions: data?.props?.additionalFunctions,
        additionalRoles: data?.props?.additionalRoles,
        researchModel: data?.props?.researchModel,
        useCase: data?.props?.useCase,
        contentType,
        contentSubtype,
        contextType,
        author: data?.props?.author,
        coAuthors: data?.props?.coAuthors,
        market: data?.props?.market,
        contextualizationOf: [parent],
        [parentField]: [parent],
        language: data?.props?.language,
        dataSource: filteredDataSources,
        dataSourceItem: filteredDataSourceItems,
        project: filteredProjects
      },
      triggerLocation: TriggerLocation.CONTEXTUALIZATION,
      readonlyFields: [`Item.status`]
    };
  };

  const existedBranches = getExistingBranchItems(
    supportedContextualizationTypes,
    contextChildren,
    selectedSubType?.id,
    selectedSubTypeContext?.id
  );

  return (
    <div className="slider-activator">
      <Modal
        id={`contextualizeModal`}
        onClose={onCancel}
        header={'Contextualize Item'}
        footer={
          <Buttons>
            <Secondary label="Cancel" onClick={onCancel} />
            <Primary
              label={'Next'}
              onClick={() => {
                showForm(getCreateItemData(), schema);
                updateUrl({ id: 'newitem' });
                onCancel();
              }}
              disabled={
                !selectedType ||
                (selectedType == ContextualizeType.Branch &&
                  !selectedSubType) ||
                (selectedType == ContextualizeType.Branch &&
                  selectedSubType &&
                  selectedSubType?.description
                    ?.toLocaleLowerCase()
                    ?.indexOf('context') != -1 &&
                  !selectedSubTypeContext?.id) ||
                (selectedType == ContextualizeType.Branch &&
                  existedBranches.length > 0) ||
                disabledContentType
              }
            />
          </Buttons>
        }
      >
        <div className="modal-container scrollable">
          <div className="select-contextualization-type">
            Contextualizing this document will copy all metadata into a new item
            and establish a relationship to this original document.
          </div>
          <div className="select-contextualization-type">Select Type</div>
          {contextualizeOptions.map(o => {
            const isDisabled =
              !supportedContextualizationTypes ||
              !supportedContextualizationTypes[o.configKey] ||
              disabledContentType;
            return (
              <>
                <div
                  className={`contextualize-option ${
                    selectedType == o.type ? `highlighted` : ``
                  } ${isDisabled ? ` disabled` : ''}`}
                  key={o.type}
                  onClick={e => {
                    if (isDisabled) return;
                    if (o.type != selectedType) {
                      setSelectedType(o.type);
                      setSelectedSubType(null);
                      setSelectedSubTypeContext(null);
                      setSupportedSubtypeContexts(null);
                    }
                  }}
                >
                  <div className="context-type">{o.type} Contextualization</div>
                  {o.type == ContextualizeType.Branch && (
                    <div className="context-description">
                      An additional layer of context that is targeted to a
                      different audience. Branches follow a specific subtype and
                      link back to the parent document.
                    </div>
                  )}
                  {o.type == ContextualizeType.Perspective && (
                    <div className="context-description">
                      A stand-alone document that summarizes key points of the
                      parent document for a different audience.
                    </div>
                  )}
                  {o.type == ContextualizeType.Variant && (
                    <div className="context-description">
                      A stand-alone document that is at least a 10-30% rewritten
                      to a different audience, preserving the same research
                      model and content type.
                    </div>
                  )}
                </div>

                {o.type == ContextualizeType.Branch &&
                  selectedType == ContextualizeType.Branch && (
                    <div className="branch-container">
                      <Typeahead
                        options={supportedSubtypes || []}
                        value={selectedSubType?.description}
                        disabled={
                          !supportedSubtypes?.length ||
                          isLoading ||
                          disabledContentType
                        }
                        renderOption={(o: any) => {
                          return (
                            <span
                              className={
                                o.id == selectedSubType ? `selected-option` : ``
                              }
                              title={o.description}
                            >
                              {o.description}
                            </span>
                          );
                        }}
                        onSelect={(selected, opts?: SROpts) => {
                          if (selected != selectedSubType) {
                            setSelectedSubType(selected);
                            setSelectedSubTypeContext(null);
                            setSupportedSubtypeContexts(null);
                          } else if (!!selected.description) {
                            opts.setText(selected.description);
                          }
                        }}
                        placeholder={'Select Branch SubType'}
                      />
                      {!!supportedSubtypeContexts &&
                        supportedSubtypeContexts?.length && (
                          <Typeahead
                            options={supportedSubtypeContexts || []}
                            value={selectedSubTypeContext?.description}
                            disabled={
                              !supportedSubtypeContexts?.length ||
                              isLoading ||
                              disabledContentType
                            }
                            renderOption={(o: any) => {
                              return (
                                <span
                                  className={
                                    o.id == selectedSubTypeContext
                                      ? `selected-option`
                                      : ``
                                  }
                                  title={o.description}
                                >
                                  {o.description}
                                </span>
                              );
                            }}
                            onSelect={selected => {
                              setSelectedSubTypeContext(selected);
                            }}
                            placeholder={'Select Context'}
                          />
                        )}

                      <BranchError
                        branches={existedBranches}
                        subType={selectedSubType}
                        ctxtType={selectedSubTypeContext}
                        onClickItem={onCancel}
                      />
                    </div>
                  )}
                <Loader />
              </>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

const getExistingBranchItems = (
  config: ContextualizationConfig,
  contextChildren: Metadata[],
  subTypeId: number,
  ctxtTypeId: number
) => {
  if (!subTypeId || MQCCUpdateSubTypes.includes(subTypeId)) return [];
  if (!contextChildren?.length) return [];
  const skipDupBranchValidation = config?.skipDuplicateBranchValidation || [];
  return contextChildren.filter(item => {
    if (!item?.props?.refId) return false;
    const itemSubType = item.props.contentSubtype?.filter(s => s?.props?.id)[0];
    const itemCtxtType = item.props.contextType?.filter(c => c?.props?.id)[0];
    const itemSubTypeId = itemSubType?.props?.id;
    if (skipDupBranchValidation.includes(itemSubTypeId)) return false;
    const itemCtxtTypeId = itemCtxtType?.props?.id;
    return subTypeId == itemSubTypeId && itemCtxtTypeId == ctxtTypeId;
  });
};

interface BEProps {
  branches: Metadata[];
  subType: SubType;
  ctxtType: SubType;
  onClickItem: () => any;
}
const BranchError = ({ branches, subType, ctxtType, onClickItem }: BEProps) => {
  if (!branches.length) return null;
  const type = ctxtType?.description
    ? `${ctxtType.description} Context`
    : subType?.description;
  const item =
    branches.filter(b => b?.props?.refId && b.props.title)[0] || branches[0];

  return (
    <Error>
      This document already has {useAorAn(type).toLowerCase()} {type}:{' '}
      <ItemLink
        refId={item?.props?.refId}
        title={item?.props?.title}
        onClick={onClickItem}
      />
    </Error>
  );
};

const createAsContextualizationParent = (
  { id, labels, props }: Partial<Metadata>,
  selectedType: ContextualizeType,
  subType: SubType,
  ctxtType: SubType
) => {
  let relationshipTypeId = null;
  let titlePreceedingText = `${selectedType} Contextualization of: `;

  switch (selectedType) {
    case ContextualizeType.Branch:
      if (subType.id == ContentSubType.MQUpdate) {
        titlePreceedingText = 'Corporate Transaction Notification: ';
      } else if (ctxtType?.description) {
        titlePreceedingText = `${ctxtType.description} Context: `;
      } else if (subType?.description) {
        titlePreceedingText = `${subType.description}: `;
      }
      if (MQCCUpdateSubTypes.includes(subType.id)) {
        relationshipTypeId = RelationshipTypeId.UpdateBranch;
      } else {
        relationshipTypeId = RelationshipTypeId.BranchContextualization;
      }
      break;
    case ContextualizeType.Perspective:
      relationshipTypeId = RelationshipTypeId.PerspectiveContextualization;
      break;
    case ContextualizeType.Variant:
      relationshipTypeId = RelationshipTypeId.VariantContextualization;
      break;
  }
  const parent: Partial<Metadata> = {
    id,
    labels,
    props,
    relationship: {
      type: 'ITEM_ITEM',
      props: {
        field: 'contextualizationOf',
        parentRelationshipTypeId: RelationshipTypeId.Contextualization,
        relationshipTypeId
      }
    }
  };
  return {
    parent,
    parentField: `contextualizationOf_${relationshipTypeId}`,
    titlePreceedingText
  };
};
